import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../utils/echarts'
import type { TFunction } from 'i18next'
import type { TaxYearlyData } from '@popety-io/types'
import { Icon } from '../../../atoms/Icon'

interface TaxBurdenEvolutionChartProps {
  data: TaxYearlyData[]
  height?: number
  t: TFunction
}

export interface TaxBurdenEvolutionChartRef {
  exportChart: () => string | null
}

export const TaxBurdenEvolutionChart = forwardRef<
  TaxBurdenEvolutionChartRef,
  TaxBurdenEvolutionChartProps
>(({ data, height = 350, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Sort data by year
    const sortedData = [...data].sort((a, b) => a.year - b.year)

    // Prepare data arrays
    const years = sortedData.map((d) => d.year.toString())
    const singleData = sortedData.map((d) =>
      (d.tax_burden_single * 100).toFixed(1)
    )
    const marriedData = sortedData.map((d) =>
      (d.tax_burden_married * 100).toFixed(1)
    )
    const familyData = sortedData.map((d) =>
      (d.tax_burden_family * 100).toFixed(1)
    )

    // Build series for all profiles
    const series: echarts.SeriesOption[] = [
      {
        name: t('profiles.single'),
        type: 'line',
        data: singleData,
        lineStyle: { color: '#3b82f6', width: 2 },
        itemStyle: { color: '#3b82f6' },
        symbol: 'circle',
        symbolSize: 5,
        smooth: true,
      },
      {
        name: t('profiles.married'),
        type: 'line',
        data: marriedData,
        lineStyle: { color: '#10b981', width: 2 },
        itemStyle: { color: '#10b981' },
        symbol: 'diamond',
        symbolSize: 5,
        smooth: true,
      },
      {
        name: t('profiles.family'),
        type: 'line',
        data: familyData,
        lineStyle: { color: '#f59e0b', width: 2 },
        itemStyle: { color: '#f59e0b' },
        symbol: 'triangle',
        symbolSize: 5,
        smooth: true,
      },
    ]

    const legendData = series.map((s) => s.name as string)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const year = params[0].axisValue
          let content = `<strong>${year}</strong><br/>`
          params.forEach((p: any) => {
            content += `<span style="color:${p.color}">●</span> ${p.seriesName}: ${p.value}%<br/>`
          })
          return content
        },
      },
      legend: {
        data: legendData,
        top: 10,
        textStyle: { fontSize: 12 },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: { fontSize: 11, color: '#1f2937' },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
      },
      yAxis: {
        type: 'value',
        name: t('charts.taxBurdenEvolution.axis.burden'),
        axisLabel: {
          fontSize: 11,
          color: '#1f2937',
          formatter: '{value}%',
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
      },
      series,
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height, t])

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.noData')}
      </div>
    )
  }

  // Calculate insights
  const sortedData = [...data].sort((a, b) => a.year - b.year)
  const firstYear = sortedData[0]
  const lastYear = sortedData[sortedData.length - 1]

  const avgChange =
    (((lastYear.tax_burden_single - firstYear.tax_burden_single) /
      firstYear.tax_burden_single) *
      100 +
      ((lastYear.tax_burden_married - firstYear.tax_burden_married) /
        firstYear.tax_burden_married) *
        100 +
      ((lastYear.tax_burden_family - firstYear.tax_burden_family) /
        firstYear.tax_burden_family) *
        100) /
    3

  return (
    <div>
      <div
        ref={chartRef}
        style={{ width: '100%', height: `${height}px`, minHeight: '300px' }}
      />

      {/* Trend insights */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icon name="trending-up" size="xs" />{' '}
          {t('charts.taxBurdenEvolution.summary.title', {
            startYear: firstYear.year,
            endYear: lastYear.year,
          })}
        </div>
        <div style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.4' }}>
          • <strong>{t('charts.taxBurdenEvolution.summary.avgChange')}:</strong>{' '}
          {avgChange >= 0 ? '+' : ''}
          {avgChange.toFixed(1)}%
          <br />•{' '}
          <strong>{t('charts.taxBurdenEvolution.summary.trend')}:</strong>{' '}
          {avgChange < -2
            ? t('charts.taxBurdenEvolution.summary.decreasing')
            : avgChange > 2
              ? t('charts.taxBurdenEvolution.summary.increasing')
              : t('charts.taxBurdenEvolution.summary.stable')}
        </div>
      </div>
    </div>
  )
})

TaxBurdenEvolutionChart.displayName = 'TaxBurdenEvolutionChart'
